import { baseApiClient } from "data/modules/api/api.client";
import { queryClient } from "data/modules/api/customQueryClient";
import { checkIsArrayEmpty } from "features/Common/modules/DataTypes/utils/Array.utils";
import { CHANGE_STATES } from "features/Common/modules/Form/constants/Form.constants";
import { MEMBERSHIP_APIS } from "../constants/Membership.apis";
import {
  CUSTOM_LINK_API_KEYS,
  MEMBERSHIP_MAPPED_LINKS_RESPONSE_KEY,
  MEMBERSHIP_TIER_DETAILS_KEYS,
  custom_link_display_strings,
} from "../constants/MembershipsSection.data";

const invalidateMembershipCustomLinksQueries = () => {
  queryClient.invalidateQueries({
    queryKey: [MEMBERSHIP_APIS.getCustomLinksByMembership],
  });
};

/**
 * Returns the custom link count label for the membership table.
 * Falls back to "View links" when the list API does not provide a count.
 */
export const getMembershipCustomLinkButtonLabel = ({ record }) => {
  const customLinksCount = record?.[MEMBERSHIP_TIER_DETAILS_KEYS.CUSTOM_LINKS];

  if (customLinksCount == null || customLinksCount === "") {
    return custom_link_display_strings.view_links_button;
  }

  return customLinksCount;
};

/**
 * Extracts the list of custom links mapped to a membership from the API response.
 */
export const getMembershipMappedCustomLinks = (data) =>
  data?.[MEMBERSHIP_MAPPED_LINKS_RESPONSE_KEY] || [];

/**
 * Computes added and removed custom links by diffing existing vs updated selections.
 */
export const extractEditedCustomLinks = ({
  existingLinks = [],
  updatedLinks = [],
}) => {
  const existingLinkMap = new Map(
    existingLinks.map((link) => [link[CUSTOM_LINK_API_KEYS.LINK_UUID], link])
  );
  const updatedLinkMap = new Map(
    updatedLinks.map((link) => [link[CUSTOM_LINK_API_KEYS.LINK_UUID], link])
  );

  const result = [];

  for (const [linkUuid, link] of existingLinkMap) {
    if (!updatedLinkMap.has(linkUuid)) {
      result.push({
        [CUSTOM_LINK_API_KEYS.LINK_UUID]: link[CUSTOM_LINK_API_KEYS.LINK_UUID],
        change_type: CHANGE_STATES.deleted,
      });
    }
  }

  for (const [linkUuid, link] of updatedLinkMap) {
    if (!existingLinkMap.has(linkUuid)) {
      result.push({
        [CUSTOM_LINK_API_KEYS.LINK_UUID]: link[CUSTOM_LINK_API_KEYS.LINK_UUID],
        change_type: CHANGE_STATES.added,
      });
    }
  }

  return result;
};

const postAddCustomLinkMembershipMapping = async ({
  communityListingUuid,
  membershipUuids,
  linkUuids,
  applyToExistingCustomers,
}) => {
  const payload = {
    membership_uuids: membershipUuids,
    link_uuids: linkUuids,
  };

  if (communityListingUuid) {
    payload.community_listing_uuid = communityListingUuid;
  }

  if (applyToExistingCustomers != null) {
    payload.apply_to_existing_customers = applyToExistingCustomers;
  }

  await baseApiClient.post(
    MEMBERSHIP_APIS.addCustomLinkMembershipMapping,
    payload
  );
};

const postRemoveCustomLinkMembershipMapping = async ({
  communityListingUuid,
  membershipUuids,
  linkUuids,
  applyToExistingCustomers,
}) => {
  const payload = {
    membership_uuids: membershipUuids,
    link_uuids: linkUuids,
  };

  if (communityListingUuid) {
    payload.community_listing_uuid = communityListingUuid;
  }

  if (applyToExistingCustomers != null) {
    payload.apply_to_existing_customers = applyToExistingCustomers;
  }

  await baseApiClient.post(
    MEMBERSHIP_APIS.removeCustomLinkMembershipMapping,
    payload
  );
};

/**
 * Syncs custom link ↔ membership mappings for a single membership using change_type diffs.
 */
export const syncCustomLinksForMembership = async ({
  communityListingUuid,
  membershipUuid,
  editedLinks,
  applyToExistingCustomers,
}) => {
  if (!editedLinks?.length || !membershipUuid) return;

  const addedLinkUuids = editedLinks
    .filter((link) => link.change_type === CHANGE_STATES.added)
    .map((link) => link[CUSTOM_LINK_API_KEYS.LINK_UUID]);

  const removedLinkUuids = editedLinks
    .filter((link) => link.change_type === CHANGE_STATES.deleted)
    .map((link) => link[CUSTOM_LINK_API_KEYS.LINK_UUID]);

  if (!checkIsArrayEmpty(addedLinkUuids)) {
    await postAddCustomLinkMembershipMapping({
      communityListingUuid,
      membershipUuids: [membershipUuid],
      linkUuids: addedLinkUuids,
      applyToExistingCustomers,
    });
  }

  if (!checkIsArrayEmpty(removedLinkUuids)) {
    await postRemoveCustomLinkMembershipMapping({
      communityListingUuid,
      membershipUuids: [membershipUuid],
      linkUuids: removedLinkUuids,
      applyToExistingCustomers,
    });
  }

  invalidateMembershipCustomLinksQueries();
};

/**
 * Syncs membership ↔ custom link mappings for a single link by diffing UUID sets.
 */
export const syncMembershipsForCustomLink = async ({
  communityListingUuid,
  linkUuid,
  initialMembershipUuids,
  selectedMembershipUuids,
  applyToExistingCustomers,
}) => {
  if (!linkUuid) return;

  const initialSet = new Set(initialMembershipUuids || []);
  const selectedSet = new Set(selectedMembershipUuids || []);

  const addedUuids = [...selectedSet].filter((uuid) => !initialSet.has(uuid));
  const removedUuids = [...initialSet].filter((uuid) => !selectedSet.has(uuid));

  if (!checkIsArrayEmpty(addedUuids)) {
    await postAddCustomLinkMembershipMapping({
      communityListingUuid,
      membershipUuids: addedUuids,
      linkUuids: [linkUuid],
      applyToExistingCustomers,
    });
  }

  if (!checkIsArrayEmpty(removedUuids)) {
    await postRemoveCustomLinkMembershipMapping({
      communityListingUuid,
      membershipUuids: removedUuids,
      linkUuids: [linkUuid],
      applyToExistingCustomers,
    });
  }

  invalidateMembershipCustomLinksQueries();
};
